import { TextButton as WSRTextButton } from '@wix/design-system'
import { ChoiceQuestionContentComponent, useContent, useMetadata, useNext } from '@wix/funnel-builder-lizard'
import { ArrowRight, ChevronLeft } from '@wix/wix-ui-icons-common/lazy'
import classNames from 'classnames'
import React, { FC, useCallback, useEffect, useState } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { createMetadataFromBusinessTerm } from '../../../category/categoryInMetadata'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { useExperiments } from '../../../hooks/useExperiments'
import { Button as HarmonyButton } from '../../reusable/Button'
import type { BusinessTermService } from '../../reusable/Category/api/BusinessTermService'
import type { SearchSource } from '../../reusable/Category/state/types'
import { DesignSystemTokensLoader } from '../../reusable/DesignSystemTokensLoader'
import WixLogo from '../../reusable/icons/WixLogo.svg'
import { Input } from '../../reusable/Input/Input'
import { RadioOption } from '../../reusable/RadioOption'
import { SkipButton } from '../../reusable/SkipButton'
import { getSectorAppIds } from '../../WixMobile/WixMobileApps/common'
import { businessTermMap } from './businessTermMap'
import centerStyles from './EasierCategory.center.scss'
import { dataHooks } from './EasierCategory.dataHooks'
import leftStyles from './EasierCategory.scss'
import { matchBusinessTermFromText } from './matchBusinessTermFromText'

type OptionContent = {
  text: string
}

export type Content = {
  layout?: 'left' | 'center'
  subtitle?: string
  backButton?: string
  title: string
  titleSize?: 'default' | 'small'
  skipButton: string
  nextButton: string
  video: string
  safariSrc?: string
  skin?: 'harmony'
  skipPosition?: 'bottomRight' | 'topRight' | 'center'
  showSkip?: boolean
  otherInputLabel: string
  otherInputPlaceholder: string
  question: ChoiceQuestionContentComponent<{}, OptionContent>
}

const OTHER_INPUT_MAX_LENGTH = 80

export const EasierCategory: FC<{ businessTermService: BusinessTermService }> = ({ businessTermService }) => {
  const [selectedOptionKey, setSelectedOptionKey] = useState<string | null>(null)
  const [clickedButton, setClickedButton] = useState<string>()
  const [otherInputValue, setOtherInputValue] = useState('')

  const { content } = useContent<Content>()
  const {
    layout = 'left',
    subtitle,
    backButton,
    title,
    titleSize,
    skipButton,
    skipPosition,
    showSkip,
    nextButton,
    video,
    safariSrc,
    otherInputLabel,
    otherInputPlaceholder,
    question,
  } = content
  const isCentered = layout === 'center'
  const isSkipInFooter = isCentered && skipPosition === 'center'
  const s = isCentered ? centerStyles : leftStyles
  const { questionKey, options: allOptions } = question
  const options = allOptions.filter(o => o.optionKey in businessTermMap)

  const next = useNext()
  const pageLogger = usePageLogger()
  const { save } = useStorageContext()
  const experiments = useExperiments()
  const [{ language }] = useMetadata()

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const handleOptionClick = useCallback(
    (optionKey: string) => {
      if (optionKey === selectedOptionKey) {
        return
      }
      void pageLogger.clickAnswer({ questionKey, buttonName: optionKey })
      setSelectedOptionKey(optionKey)
    },
    [selectedOptionKey, pageLogger, questionKey],
  )

  const handleNextClick = useCallback(async () => {
    if (clickedButton || !selectedOptionKey) {
      return
    }
    setClickedButton('primary')

    const selectedIndex = options.findIndex(o => o.optionKey === selectedOptionKey)
    const entry = businessTermMap[selectedOptionKey]
    if (!entry) {
      console.error(`No business term mapping found for option key: ${selectedOptionKey}`)
      return
    }

    const isOther = selectedOptionKey === 'other'
    const freeText = isOther && otherInputValue ? otherInputValue : undefined
    const presetAppDefIds = entry.appDefId ? [entry.appDefId] : []

    const matched = freeText ? await matchBusinessTermFromText({ businessTermService, text: freeText, language }) : null

    const { businessTerm, businessSegmentId } = matched ?? entry
    const appDefIds = matched ? getSectorAppIds(businessSegmentId, experiments) : presetAppDefIds
    const search = freeText && matched ? freeText : ''
    const searchSource: SearchSource = matched ? 'input' : 'select'

    const metadata = createMetadataFromBusinessTerm(businessTerm, businessSegmentId)

    save({
      category: {
        search,
        searchSource,
        businessTerm,
        businessSegmentId,
        index: selectedIndex,
      },
      appDefIds,
    })

    void pageLogger.answerCategory({
      businessTerm,
      segment: metadata.segment,
      businessSegmentId,
      userSearchInput: freeText,
    })

    void pageLogger.answer({
      answer: businessTerm,
      position: selectedIndex + 1,
      answerType: isOther ? 'free text' : undefined,
      freeText,
    })

    const answerValues = businessTerm.displayName ? [businessTerm.displayName] : []
    next({ [questionKey]: { values: answerValues } }, metadata)
  }, [
    clickedButton,
    selectedOptionKey,
    options,
    pageLogger,
    questionKey,
    next,
    save,
    otherInputValue,
    experiments,
    language,
    businessTermService,
  ])

  const handleSkipClick = useCallback(() => {
    if (clickedButton) {
      return
    }
    setClickedButton('skip')

    void pageLogger.generalAction({ action: 'skip' })

    next({ [questionKey]: { skipped: true } })
  }, [clickedButton, pageLogger, next, questionKey])

  const handleBackClick = useCallback(() => {
    if (clickedButton) {
      return
    }
    setClickedButton('back')
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }, [clickedButton, pageLogger])

  const continueButton = (
    <HarmonyButton
      className={isCentered ? undefined : s.nextButton}
      size="medium"
      skin="standard"
      onClick={handleNextClick}
      disabled={!selectedOptionKey}
      isLoading={clickedButton === 'primary'}
      dataHook={dataHooks.nextButton()}
      label={nextButton}
    />
  )

  const skipControl = (
    <button type="button" className={s.skipButton} onClick={handleSkipClick} data-hook={dataHooks.skip()}>
      {skipButton}
    </button>
  )

  const continueControls = isSkipInFooter ? (
    <div className={s.continueControls}>
      {showSkip !== false && skipControl}
      {continueButton}
    </div>
  ) : (
    continueButton
  )

  return (
    <DesignSystemTokensLoader type="odeditor">
      <div className={s.root} data-hook={dataHooks.template()}>
        <header className={s.header}>
          {!isCentered && (
            <>
              <button className={s.backBtn} onClick={handleBackClick}>
                <ChevronLeft />
              </button>
              <hr className={s.divider} />
            </>
          )}
          <WixLogo width={isCentered ? 47 : 46} height={18} />
        </header>

        <div className={s.content}>
          <div className={s.left}>
            <div className={s.card}>
              <div className={s.cardContent}>
                <h1
                  className={classNames(s.title, { [leftStyles.titleSmall]: !isCentered && titleSize === 'small' })}
                  data-hook={dataHooks.title()}
                >
                  {title}
                </h1>

                {isCentered && subtitle && <p className={s.subtitle}>{subtitle}</p>}
                <div className={s.options}>
                  {options.map((option, index) => (
                    <RadioOption
                      key={option.optionKey}
                      name={questionKey}
                      value={option.optionKey}
                      checked={option.optionKey === selectedOptionKey}
                      onChange={() => handleOptionClick(option.optionKey)}
                      label={option.text}
                      dataHook={dataHooks.radio({ index })}
                      className={s.radioOption}
                    />
                  ))}
                </div>

                {selectedOptionKey === 'other' && (
                  <div className={s.otherInputBlock} data-hook={dataHooks.otherInputBlock()}>
                    <span className={s.otherInputLabel}>{otherInputLabel}</span>
                    <Input
                      type="text"
                      autoFocus
                      maxLength={OTHER_INPUT_MAX_LENGTH}
                      value={otherInputValue}
                      onChange={e => setOtherInputValue(e.target.value)}
                      placeholder={otherInputPlaceholder}
                      data-hook={dataHooks.otherInput()}
                      skin="harmony"
                    />
                  </div>
                )}

                {!isCentered && continueButton}
              </div>
              {isCentered && (
                <footer className={s.footer}>
                  <button
                    type="button"
                    className={s.backButton}
                    onClick={handleBackClick}
                    aria-label={backButton || 'Back'}
                  >
                    <ChevronLeft />
                    {backButton}
                  </button>
                  {continueControls}
                </footer>
              )}
            </div>
          </div>
          <div className={s.right}>
            <div className={s.videoContainer}>
              {video && (
                <video className={s.video} autoPlay muted playsInline aria-hidden={isCentered || undefined}>
                  {safariSrc && <source src={safariSrc} type='video/quicktime; codecs="hvc1"' />}
                  <source src={video} />
                </video>
              )}
            </div>
          </div>
        </div>

        {!isSkipInFooter &&
          showSkip !== false &&
          (isCentered ? (
            skipControl
          ) : skipPosition === 'topRight' ? (
            <SkipButton skin="harmony" skipButton={skipButton} onClick={handleSkipClick} dataHook={dataHooks.skip()} />
          ) : (
            <div className={s.skip}>
              <WSRTextButton
                skin="dark"
                size="small"
                onClick={handleSkipClick}
                suffixIcon={<ArrowRight />}
                dataHook={dataHooks.skip()}
              >
                {skipButton}
              </WSRTextButton>
            </div>
          ))}
      </div>
    </DesignSystemTokensLoader>
  )
}
